import { snakeCase, transform } from 'lodash';
import getReturnMerchants from './getReturnMerchants';
import { RequestMerchant, ReturnItem } from '../../types/order';
import { Address } from '../../types/address';
import ReturnMethodStore from '../ReturnMethodStore';
import { HubbedLocation } from '../../types/hubbed';
import { DropoffLocation } from '../../types/dropoff-location';

interface Products {
  quantity: string | undefined;
  comments: string | null | undefined;
  returns_reason_id: number;
  price: string;
  name: string;
  options: Array<{ id: string; label: string; value: string }>;
  shippit_connect_api_reference: string;
  sku: string;
  returns_condition_id: number;
}

function buildProducts(products: Array<ReturnItem>): Array<Products> {
  return products.map(item => ({
    name: item.name,
    price: item.original_price!.toString(),
    sku: item.sku,
    shippit_connect_api_reference: item.api_reference!.toString(),
    options: item.options!,
    quantity: item.returnDetails!.quantity,
    returns_reason_id: item.returnDetails!.reason,
    returns_condition_id: item.returnDetails!.condition,
    comments: item.returnDetails!.comments,
  }));
}

function getCarrierType(returnMethod: ReturnMethodStore): string | undefined {
  if (
    returnMethod.getMethod() &&
    !returnMethod.isSelectedMethodReturnInStore()
  ) {
    return returnMethod.getMethod().getType();
  }

  return undefined;
}
interface PudoBusinessHour {
  day: string;
  open_time: string;
  close_time: string;
}

interface PudoLocation {
  external_id: string;
  external_name?: string;
  address1: string;
  suburb: string | undefined;
  state: string | undefined;
  postcode: string | undefined;
  country_code: string;
  latitude: string | undefined;
  longitude: string | undefined;
  business_hours?: PudoBusinessHour[] | undefined;
}

export interface ReturnPayload {
  return_merchants: Array<number>;
  order: {
    request_id: number;
    merchant_id: number;
    purchase_reference: string;
    dropoff: boolean | undefined;
    from: {
      email: string;
      phone: string | null | undefined;
      given_name: string | undefined;
      family_name: string | undefined;
      address: string;
      locality: string | undefined;
      state_code: string | undefined;
      country_code: string | undefined;
      postcode: string | undefined;
      address_type: string;
      instructions: string;
    };
    products: Array<Products>;
    carrier: {
      carrier_type: string | undefined;
    };
    pudo_location: PudoLocation | undefined;
    dropoff_location_external_id?: string;
    refund_method_id?: string;
    return_in_store?: boolean;
    self_return?: boolean;
  };
}

export interface ReturnPayloadParams {
  returnRequestId: number;
  merchantId: number;
  orderReference: string;
  merchants: Array<RequestMerchant>;
  products: Array<ReturnItem>;
  customerEmail: string;
  shippingDetails: Address;
  returnMethod: ReturnMethodStore;
  refundMethodId: number | null;
}

const toPudoLocation = (
  hubbedLocation: HubbedLocation | undefined,
  dropoffLocation: DropoffLocation | undefined
): PudoLocation | undefined => {
  if (hubbedLocation) {
    return {
      external_id: hubbedLocation.locationId,
      address1: hubbedLocation.address.addressLine1,
      suburb: hubbedLocation.address.suburb,
      state: hubbedLocation.address.state,
      postcode: hubbedLocation.address.postCode,
      country_code: hubbedLocation.address.countryCode,
      latitude: hubbedLocation.latitude,
      longitude: hubbedLocation.longitude,
    };
  }

  if (dropoffLocation) {
    const businessHours = dropoffLocation.businessHours?.map(business_hour =>
      transform(business_hour, (acc: any, value, key) => {
        const snakeKey = snakeCase(key);
        acc[snakeKey] = value;
      })
    );

    return {
      external_id: dropoffLocation.externalId,
      external_name: dropoffLocation.externalName,
      address1: dropoffLocation.addressLine1,
      suburb: dropoffLocation.town,
      state: dropoffLocation.zone,
      postcode: dropoffLocation.postcode,
      country_code: dropoffLocation.countryCode,
      latitude: dropoffLocation.latitude
        ? `${dropoffLocation.latitude}`
        : undefined,
      longitude: dropoffLocation.longitude
        ? `${dropoffLocation.longitude}`
        : undefined,
      business_hours: businessHours,
    };
  }

  return undefined;
};

export default function getReturnPayload(
  params: ReturnPayloadParams
): ReturnPayload {
  const {
    returnRequestId,
    merchantId,
    orderReference,
    merchants,
    products,
    customerEmail,
    shippingDetails,
    returnMethod,
    refundMethodId,
  } = params;

  const hubbedLocation =
    returnMethod.getMethod() && returnMethod.getMethod().isHubbed()
      ? returnMethod.getHubbedMethod().getHubbedLocation()
      : undefined;

  const dropoffLocation =
    returnMethod.getMethod() && returnMethod.getMethod().isDropOff()
      ? returnMethod.getDropOffMethod().getDropoffLocation()
      : undefined;

  const dropoffLocationExternalId = dropoffLocation?.externalId;

  const pudoLocation = toPudoLocation(hubbedLocation, dropoffLocation);

  const hubbedFromAddress = hubbedLocation
    ? {
        address: hubbedLocation.address.addressLine1,
        locality: hubbedLocation.address.suburb,
        state_code: hubbedLocation.address.state,
        country_code: hubbedLocation.address.countryCode,
        postcode: hubbedLocation.address.postCode,
        address_type: '',
      }
    : {};

  return {
    return_merchants: getReturnMerchants(merchants, products),
    order: {
      request_id: returnRequestId,
      merchant_id: merchantId, // this may or may not be master merchant
      purchase_reference: orderReference,
      dropoff: returnMethod.getMethod()
        ? returnMethod.getMethod().isDropOff()
        : undefined,
      from: {
        email: customerEmail,
        phone: shippingDetails.phone,
        given_name: shippingDetails.firstname,
        family_name: shippingDetails.lastname,
        address: (shippingDetails.street || []).join(', '),
        locality: shippingDetails.suburb,
        state_code: shippingDetails.region_code,
        country_code: shippingDetails.country_code,
        postcode: shippingDetails.postcode,
        address_type: shippingDetails.address_type || '',
        instructions: shippingDetails.instructions || '',
        ...hubbedFromAddress,
      },
      carrier: {
        carrier_type: getCarrierType(returnMethod),
      },
      pudo_location: pudoLocation,
      dropoff_location_external_id: dropoffLocationExternalId,
      refund_method_id: refundMethodId ? refundMethodId.toString() : undefined,
      products: buildProducts(products),
      return_in_store: returnMethod.isSelectedMethodReturnInStore(),
      self_return: returnMethod.isSelectedMethodSelfReturn(),
    },
  };
}
